import React from "react";
import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Calendar, DollarSign, LinkIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { updateCoach } from "~/lib/server/coach";
import {
  type GetCalendarResponse,
  getCalendar,
  getCalendars,
} from "~/lib/ghl/get-calendars";
import { getLocationAccessToken } from "~/lib/ghl/get-location-access-token";
import { useCoachPrivateProfileRequired } from "~/contexts/coach";
import { requireCoachAuth } from "~/lib/coach-middleware";
import { updateContactMode } from "~/lib/functions/messaging";

// Calendar server functions
export const fetchCalendars = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .validator(z.object({ locationId: z.string() }))
  .handler(async ({ data }) => {
    const { locationId } = data;
    if (!locationId) {
      return { error: "No GHL location ID found", calendars: [] };
    }
    try {
      const accessToken = await getLocationAccessToken(locationId);
      const { calendars } = await getCalendars(accessToken, {
        locationId,
        showDrafted: false,
      });
      if (0 === calendars.length) {
        return { error: "No calendars found for location", calendars: [] };
      }
      return {
        calendars: calendars
          .filter((c) => false !== c.isActive)
          .map((c) => ({ id: c.id, name: c.name })),
      };
    } catch (error) {
      return {
        error:
          error instanceof Error && error.message.includes("AbortError")
            ? "Calendar request timed out. Please try again later."
            : "Failed to fetch calendars. Please try again later.",
        calendars: [],
      };
    }
  });

export const fetchCalendarDetails = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .validator(z.object({ locationId: z.string(), calendarId: z.string() }))
  .handler(
    async ({ data }): Promise<GetCalendarResponse | { error: string }> => {
      const { locationId, calendarId } = data;
      if (!locationId) {
        return { error: "No GHL location ID found" };
      }
      if (!calendarId) {
        return { error: "No calendar selected" };
      }
      try {
        const accessToken = await getLocationAccessToken(locationId);
        const ghlCalendar = await getCalendar(accessToken, calendarId);
        return { calendar: ghlCalendar.calendar };
      } catch (error) {
        return {
          error:
            error instanceof Error && error.message.includes("AbortError")
              ? "Calendar details request timed out. Please try again later."
              : "Failed to fetch calendar details. Please try again later.",
        };
      }
    },
  );

export const updateCalendar = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .validator(z.object({ calendarId: z.string() }))
  .handler(async ({ data, context }) => {
    const { calendarId } = data;
    const coach = context.coach;
    if (!coach) {
      throw new Error("Coach not found");
    }
    if (coach.ghlCalendarId === calendarId) {
      return { success: true, ghlCalendarId: calendarId };
    }
    const accessToken = await getLocationAccessToken(coach.ghlLocationId!);
    const ghlCalendar = await getCalendar(accessToken, calendarId);
    if (false === ghlCalendar.calendar.isActive) {
      throw new Error("Selected calendar is not active");
    }
    await updateCoach(coach.id, { ghlCalendarId: calendarId });
    return { success: true, ghlCalendarId: calendarId };
  });

export const Route = createFileRoute(
  "/coach/_backend/edit-profile/client-flow/",
)({
  head: () => ({
    title: "Client Flow Setup – Dream Coach Match",
    meta: [
      {
        name: "description",
        content:
          "Configure your calendar and payment settings for client bookings",
      },
    ],
  }),
  component: ClientFlowPage,
});

const ContactModeFormSchema = z.object({
  contactMode: z.enum(["booking", "messaging"]),
  ctaButtonText: z
    .string()
    .min(1, "CTA button text is required")
    .max(50, "CTA button text must be 50 characters or less"),
});

type ContactModeFormData = z.infer<typeof ContactModeFormSchema>;

function ClientFlowPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const coach = useCoachPrivateProfileRequired();
  const [selectedCalendarId, setSelectedCalendarId] = React.useState<string>(
    coach.ghlCalendarId ?? "none",
  );

  // Update selected calendar when coach data changes
  React.useEffect(() => {
    setSelectedCalendarId(coach.ghlCalendarId ?? "none");
  }, [coach.ghlCalendarId]);

  const {
    register: registerContactMode,
    handleSubmit: handleSubmitContactMode,
    setValue: setContactModeValue,
    watch: watchContactMode,
    formState: contactFormState,
  } = useForm<ContactModeFormData>({
    resolver: zodResolver(ContactModeFormSchema),
    defaultValues: {
      contactMode: coach.profile?.contactMode ?? "booking",
      ctaButtonText: coach.profile?.ctaButtonText ?? "Book a discovery call",
    },
  });

  const selectedContactMode = watchContactMode("contactMode");

  const calendarsQuery = useQuery({
    queryKey: ["calendars", coach.ghlLocationId],
    queryFn: () =>
      fetchCalendars({ data: { locationId: coach.ghlLocationId! } }),
    enabled: !!coach.ghlLocationId && selectedContactMode === "booking",
  });

  const calendarDetailsQuery = useQuery({
    queryKey: ["calendar-details", coach.ghlLocationId, coach.ghlCalendarId],
    queryFn: () =>
      fetchCalendarDetails({
        data: {
          locationId: coach.ghlLocationId!,
          calendarId: coach.ghlCalendarId!,
        },
      }),
    enabled: !!(
      coach.ghlLocationId &&
      coach.ghlCalendarId &&
      selectedContactMode === "booking"
    ),
  });

  const updateCalendarMutation = useMutation({
    mutationFn: (calendarId: string) =>
      updateCalendar({ data: { calendarId } }),
    onSuccess: () => {
      toast.success("Calendar updated successfully");
      queryClient.invalidateQueries({ queryKey: ["coach"] });
      router.invalidate();
    },
    onError: (error) => {
      console.error("Failed to update calendar:", error);
      toast.error("Failed to update calendar");
    },
  });

  const updateContactModeMutation = useMutation({
    mutationFn: (data: ContactModeFormData) => updateContactMode({ data }),
    onSuccess: () => {
      toast.success("Contact mode updated successfully");
      queryClient.invalidateQueries({ queryKey: ["backend-coach"] });
    },
    onError: () => {
      toast.error("Failed to update contact mode");
    },
  });

  const calendarConnected = !!coach.ghlCalendarId;
  const stripeConnected = !!coach?.stripePayoutsEnabled;
  const chargesEnabled = !!coach?.stripeChargesEnabled;

  const statuses = [
    { icon: Calendar, label: "Calendar", ready: calendarConnected },
    { icon: LinkIcon, label: "Stripe Connect", ready: stripeConnected },
    { icon: DollarSign, label: "Charges Enabled", ready: chargesEnabled },
  ];

  const onSubmitContactMode = (data: ContactModeFormData) => {
    updateContactModeMutation.mutate(data);
  };

  return (
    <div className="container mx-auto space-y-8">
      <h1 className="font-heading text-3xl">Client Flow</h1>

      {/* Contact Mode Selection */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-4">
          <h3 className="font-medium">Contact Method</h3>
          <p className="text-text-tertiary text-sm">
            Choose how visitors can reach out to you from your profile page.
          </p>
        </div>

        <form
          onSubmit={handleSubmitContactMode(onSubmitContactMode)}
          className="space-y-4"
        >
          <div className="space-y-4">
            <div>
              <label
                htmlFor="contactMode"
                className="block text-sm font-medium mb-2"
              >
                Contact Method
              </label>
              <Select
                {...registerContactMode("contactMode")}
                value={selectedContactMode}
                onValueChange={(value) =>
                  setContactModeValue(
                    "contactMode",
                    value as "booking" | "messaging",
                  )
                }
                disabled={updateContactModeMutation.isPending}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select contact method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="booking">Book a Discovery Call</SelectItem>
                  <SelectItem value="messaging">Send a Message</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-text-tertiary text-xs mt-1">
                {selectedContactMode === "booking"
                  ? "Visitors will see your custom button text that opens your calendar"
                  : "Visitors will see your custom button text to send you a message"}
              </p>
              {contactFormState.errors.contactMode && (
                <p className="text-red-600 text-sm mt-1">
                  {contactFormState.errors.contactMode.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="ctaButtonText"
                className="block text-sm font-medium mb-2"
              >
                Button Text
              </label>
              <Input
                {...registerContactMode("ctaButtonText")}
                placeholder="e.g., Book a discovery call, Contact me, Get started"
                disabled={updateContactModeMutation.isPending}
                className={
                  contactFormState.errors.ctaButtonText ? "border-red-500" : ""
                }
              />
              <p className="text-text-tertiary text-xs mt-1">
                This text will appear on the button that visitors see on your
                profile
              </p>
              {contactFormState.errors.ctaButtonText && (
                <p className="text-red-600 text-sm mt-1">
                  {contactFormState.errors.ctaButtonText.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-primary hover:bg-primary/90 rounded-md px-4 py-2 text-white disabled:opacity-50"
              disabled={updateContactModeMutation.isPending}
            >
              {updateContactModeMutation.isPending
                ? "Saving..."
                : "Save Changes"}
            </button>
          </div>
        </form>
      </div>

      <div className="flex flex-col items-center justify-center gap-2">
        <video
          className="mx-auto flex aspect-video h-64 max-w-full items-center justify-center rounded-lg border bg-gray-100"
          src="https://storage.googleapis.com/msgsndr/AOzAVlvHlRJyZtr5j7Xo/media/68089b66437290636cc503be.mp4"
          controls
        />
        <p className="text-text-tertiary text-sm">
          Watch this video to understand how to set up your calendar and connect
          to our payment processor Stripe.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {statuses.map(({ icon: Icon, label, ready }) => {
          if ("Calendar" === label) {
            return (
              <Card key={label} className="cursor-pointer">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar />
                    {label}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedContactMode !== "booking" ? (
                    <div className="p-4 text-center text-sm text-gray-600">
                      Calendar not needed for messaging mode
                    </div>
                  ) : calendarsQuery.isPending ? (
                    <div className="p-4 text-center text-sm">
                      Loading calendars...
                    </div>
                  ) : calendarsQuery.error ? (
                    <div className="p-4 text-center text-sm text-red-600">
                      {calendarsQuery.error.message}
                    </div>
                  ) : calendarsQuery.data?.error ? (
                    <div className="p-4 text-center text-sm text-red-600">
                      {calendarsQuery.data.error}
                    </div>
                  ) : calendarsQuery.data &&
                    selectedContactMode === "booking" ? (
                    <>
                      <div className="space-y-2">
                        <Select
                          value={selectedCalendarId}
                          onValueChange={setSelectedCalendarId}
                          disabled={updateCalendarMutation.isPending || false}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a calendar" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {calendarsQuery.data.calendars.map((cal) => (
                              <SelectItem key={cal.id} value={cal.id}>
                                {cal.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <button
                          type="button"
                          onClick={() =>
                            updateCalendarMutation.mutate(
                              "none" === selectedCalendarId
                                ? ""
                                : selectedCalendarId,
                            )
                          }
                          className="bg-primary hover:bg-primary/90 w-full rounded-md px-4 py-2 text-white disabled:opacity-50"
                          disabled={updateCalendarMutation.isPending || false}
                        >
                          Save
                        </button>
                      </div>
                      {calendarDetailsQuery.data &&
                        "calendar" in calendarDetailsQuery.data &&
                        calendarDetailsQuery.data.calendar && (
                          <div className="mt-2 text-center text-sm">
                            Selected: {calendarDetailsQuery.data.calendar.name}
                          </div>
                        )}
                    </>
                  ) : null}
                </CardContent>
              </Card>
            );
          }

          const href = "/coach/stripe-connect";
          return (
            <Link to={href} key={label}>
              <Card className="cursor-pointer">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Icon />
                    {label}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {ready ? "Connected" : "Not Connected"}
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
